import {useNavigate} from 'react-router-dom';
import playgroundApi from "../../../services/playgroundApi";
import {appPagePaths, lessonPageUrlFragment} from "../../../config/config";
import {useState} from 'react';
import {useAppSelector} from "../../../redux/hooks";
import {myProfileStore} from "../../../redux/my-profile/myProfileStore";

export interface ILibraryLessonCardNavigationLogic {
  isCheckingWhetherLessonIsAccessible: boolean;
  onArrangementClick: (lessonId: number) => (_?: React.MouseEvent) => void;
}

/**
 * I'm not quite sure where this hook belongs.
 * It's used by the playlist page and the music home page.
 * It's also specific to the library lesson card,
 * since no other card will need to
 * check whether the user has access to the lesson before navigating to the lesson.
 *
 * I'm also not quite sure what to call the hook itself.
 */
export const useLibraryLessonCardNavigationLogic = (): ILibraryLessonCardNavigationLogic => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const doesUserMembershipGrantAccessToSongLibrary = useAppSelector(myProfileStore.getMyProfileMembershipIsSongLibraryAccessible);
  const isUserMembershipActive = useAppSelector(myProfileStore.getMyProfileMembershipIsMember);
  const isFullLibraryAccessible = doesUserMembershipGrantAccessToSongLibrary && isUserMembershipActive;

  const onArrangementClick = (lessonId: number) => async (_?: React.MouseEvent) => {
    if (isFullLibraryAccessible) {
      return navigate(`${lessonPageUrlFragment}${lessonId}`);
    }

    if (isLoading) {
      // Avoid sending multiple API requests simultaneously
      return;
    }
    
    setIsLoading(true);
    
    try {
      // If the user lacks "all songs" access, check if user has access to this arrangement
      const {
        isArrangementAccessible,
        songId,
      } = await playgroundApi.arrangementAccessCheck({arrangementId: lessonId});

      if (!isArrangementAccessible) {
        // Take the user to the song details page, with the arrangement selected
        return navigate(`${appPagePaths.songsPage}/${songId}/${lessonId}`);
      }

      // Go to the lesson player
      navigate(`${lessonPageUrlFragment}${lessonId}`);
    } finally {
      setIsLoading(false);
    }
  }
  
  return {
    isCheckingWhetherLessonIsAccessible: isLoading,
    onArrangementClick,
  };
}
